import React, { useState } from 'react'
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Alert, 
  InputAdornment, 
  IconButton,
  CircularProgress,
  Link,
  Divider,
  Checkbox,
  FormControlLabel
} from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../contexts/AuthContext'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import LockOutlinedIcon from '@mui/icons-material/LockOutlined'
import PhoneIcon from '@mui/icons-material/Phone'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'

const SignIn = ({ onSuccess, switchToSignUp, switchToPhone }) => {
  const { signIn, loading } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [showPassword, setShowPassword] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)
  
  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Form validation
    if (!email || !password) {
      setError('E-post och lösenord krävs')
      return
    }
    
    if (!termsAccepted) {
      setError('Du måste acceptera användarvillkoren för att logga in')
      return
    }
    
    // Reset error before attempting signin
    setError(null)
    
    const { error } = await signIn(email, password)
    
    if (error) {
      setError(error.message)
    } else {
      if (onSuccess) onSuccess()
    }
  }
  
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }
  
  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button 
          startIcon={<ArrowBackIcon />}
          onClick={switchToPhone}
          sx={{ mr: 2 }}
        >
          Tillbaka
        </Button>
        <Typography 
          variant="h5" 
          component="h2" 
          fontWeight="600"
        >
          Logga in med e-post
        </Typography>
      </Box>
      
      {error && (
        <Alert 
          severity="error" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-icon': { alignItems: 'center' }
          }}
        >
          {error}
        </Alert>
      )}
      
      <TextField
        label="E-postadress"
        type="email"
        fullWidth
        margin="normal"
        required
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        autoComplete="email"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <MailOutlineIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />
      
      <TextField
        label="Lösenord"
        type={showPassword ? "text" : "password"}
        fullWidth
        margin="normal"
        required
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <LockOutlinedIcon color="action" fontSize="small" />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="visa/dölj lösenord"
                onClick={togglePasswordVisibility}
                edge="end"
              >
                {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <FormControlLabel
        control={
          <Checkbox 
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            color="primary"
          />
        }
        label={
          <Typography variant="body2">
            Jag har läst och godkänner{' '}
            <Link 
              component="button"
              variant="body2"
              onClick={(e) => {
                e.preventDefault()
                navigate('/terms')
              }}
              sx={{ 
                textDecoration: 'underline',
                color: 'primary.main',
                cursor: 'pointer'
              }}
            >
              användarvillkoren
            </Link>
          </Typography>
        }
        sx={{ mb: 2 }}
      />
      
      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading}
        sx={{ 
          mt: 2, 
          mb: 3,
          height: 48
        }}
      >
        {loading ? <CircularProgress size={24} color="inherit" /> : 'Logga in'}
      </Button>


      
      <Box sx={{ mt: 3, textAlign: 'center' }}>
        <Button
          variant="text"
          onClick={switchToSignUp}
          sx={{ 
            textTransform: 'none',
            fontWeight: 500,
            color: 'primary.main'
          }}
        >
          Har du inget konto? Registrera dig
        </Button>
      </Box>
    </Box>
  )
}

export default SignIn 